import { Skeleton } from "@outschool/backpack";
import {
  ContentfulLandingPageAboveTheFoldCsrQuery,
  ContentfulLandingPageAboveTheFoldCsrQueryVariables
} from "@outschool/gql-frontend-generated";
import { useLocale } from "@outschool/localization";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { lazyWithRetry, useDeviceTypeContext } from "@outschool/ui-utils";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import NotFoundPage from "../NotFoundPage";
import { MAX_WIDTH } from "./constants";
import LandingPageHeroSection, {
  CONTENTFUL_LANDING_PAGE_HERO_FRAGMENT
} from "./LandingPageHeroSection";
import LandingPageLayout from "./LandingPageLayout";
import { LandingPage as LandingPageType } from "./types";

const LandingPageBelowTheFold = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "LandingPageBelowTheFold" */ "./LandingPageBelowTheFold"
    )
);

const CONTENTFUL_LANDING_PAGE_ABOVE_THE_FOLD = gql`
  query ContentfulLandingPageAboveTheFoldCSR($slug: String, $locale: String) {
    contentfulLandingPageCollection(
      limit: 1
      skip: 0
      where: { slug: $slug }
      locale: $locale
    ) {
      items {
        title
        metaTagCanonicalUrl
        metaTagDescription
        metaTagSharedLinkRichPreviewImage {
          url
        }
        navbarCtaUrl
        navbarCtaUrlLoggedIn
        navbarCtaText
        sectionsCollection(limit: 1) {
          items {
            __typename
            ... on ContentfulLandingPageHeroSection {
              ...LandingPageHeroSectionCSR
            }
          }
        }
      }
    }
  }
  ${CONTENTFUL_LANDING_PAGE_HERO_FRAGMENT}
`;

export default function LandingPage() {
  const { slug } = useParams<{ slug: string }>();

  const { deviceType } = useDeviceTypeContext();

  const locale = useLocale();

  const [isLoaded, setIsLoaded] = useState(false);

  const { data, loading } = useQueryWithPreviousData<
    ContentfulLandingPageAboveTheFoldCsrQuery,
    ContentfulLandingPageAboveTheFoldCsrQueryVariables
  >(CONTENTFUL_LANDING_PAGE_ABOVE_THE_FOLD, {
    variables: { slug, locale },
    fetchPolicy: "network-only",
    skip: !slug
  });

  useEffect(() => {
    if (!isLoaded) {
      setIsLoaded(true);
    }
  }, [isLoaded]);

  if (!slug) {
    return <NotFoundPage />;
  }

  const landingPage = data?.contentfulLandingPageCollection?.items?.[0];
  const section = landingPage?.sectionsCollection
    ?.items?.[0] as LandingPageType["sections"][number];

  if (!loading && !landingPage) {
    return <NotFoundPage />;
  }

  return (
    <LandingPageLayout
      title={landingPage?.title || ""}
      metaTags={{
        description: landingPage?.metaTagDescription || "",
        canonicalUrl: landingPage?.metaTagCanonicalUrl || "",
        image: landingPage?.metaTagSharedLinkRichPreviewImage?.url || ""
      }}
      deviceType={deviceType}
    >
      <>
        {!loading && isLoaded ? (
          <LandingPageHeroSection {...section} />
        ) : (
          <Skeleton variant="rectangular" width={MAX_WIDTH} height={500} />
        )}

        {!isLoaded ? (
          <Skeleton variant="rectangular" width={MAX_WIDTH} height={500} />
        ) : (
          <LandingPageBelowTheFold />
        )}
      </>
    </LandingPageLayout>
  );
}
